import { useState } from "react";
import { toast } from "sonner";
import { ScanLine, Zap, ListChecks, Trash2, CheckCircle2, ArrowDownToLine, ArrowUpFromLine, ClipboardCheck, Search } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, Panel, IssuesAlert, QtyStepper, TH } from "@/components/common";
import { CameraButton } from "@/components/CameraScanner";
import { useScanHub } from "@/components/ScanHub";
import { useKeyboardScanner } from "@/hooks/useKeyboardScanner";
import { api, apiError, apiIssues, fmtQty, lookupCode } from "@/lib/api";
import { scanFeedback } from "@/lib/feedback";

const BURST_TYPES = [
  { id: "sortie", label: "Sortie", icon: ArrowUpFromLine },
  { id: "entree", label: "Entrée", icon: ArrowDownToLine },
  { id: "ajustement", label: "Inventaire", icon: ClipboardCheck },
];

function Segmented({ value, onChange, options, testid }) {
  return (
    <div className="inline-flex rounded-md border bg-white p-1" data-testid={testid}>
      {options.map(({ id, label, icon: Icon }) => (
        <button key={id} type="button" onClick={() => onChange(id)} data-testid={`${testid}-${id}`}
          className={`flex h-11 items-center gap-2 rounded px-4 text-sm font-semibold transition-colors ${value === id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
          {Icon && <Icon className="h-4 w-4" />}{label}
        </button>
      ))}
    </div>
  );
}

const toAction = (l, type, note) => ({
  type: l.kind === "kit" ? (type === "sortie" ? "sortie_kit" : type) : type,
  code: l.item.code, quantity: Number(l.qty) || 0, note: note || null,
});

function BurstSummary({ open, summary, busy, onClose, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="burst-summary-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase">Récapitulatif du lot</DialogTitle>
          <DialogDescription>Le lot est appliqué en une seule fois : tout ou rien.</DialogDescription>
        </DialogHeader>
        {summary && <>
          <ul className="max-h-72 space-y-1.5 overflow-auto rounded border bg-slate-50 p-4 font-mono text-sm" data-testid="burst-summary-lines">
            {summary.lines.map((l, i) => <li key={i}>{l}</li>)}
          </ul>
          <IssuesAlert title="Lot bloqué — aucune modification ne sera faite" issues={summary.issues} testid="burst-summary-issues" />
        </>}
        <DialogFooter className="gap-2">
          <Button variant="outline" className="h-12" onClick={onClose} data-testid="burst-summary-cancel">Annuler</Button>
          <Button className="h-12 min-w-[160px] gap-2" disabled={busy || !summary?.can_confirm} onClick={onConfirm} data-testid="burst-summary-confirm"><CheckCircle2 className="h-4 w-4" /> Valider le lot</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BurstTable({ lines, setQty, remove, testid }) {
  if (!lines.length) return <p className="p-8 text-center text-sm text-slate-500" data-testid={`${testid}-empty`}>Scannez des articles : chaque scan ajoute 1 à la ligne correspondante.</p>;
  return (
    <div className="overflow-x-auto">
      <Table data-testid={testid}>
        <TableHeader><TableRow><TableHead className={TH}>Code</TableHead><TableHead className={TH}>Désignation</TableHead><TableHead className={`${TH} text-right`}>Stock</TableHead><TableHead className={TH}>Quantité</TableHead><TableHead /></TableRow></TableHeader>
        <TableBody>
          {lines.map((l) => (
            <TableRow key={l.key} data-testid={`burst-row-${l.item.code}`} className={l.fresh ? "sk-row-bump" : ""}>
              <TableCell className="font-mono font-semibold">{l.item.code}{l.kind === "kit" && <span className="ml-2 rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-purple-800">Kit</span>}</TableCell>
              <TableCell>{l.item.designation}</TableCell>
              <TableCell className="text-right font-mono text-slate-500">{l.kind === "kit" ? `${l.item.buildable} réal.` : fmtQty(l.item.stock)}</TableCell>
              <TableCell><QtyStepper size="sm" value={l.qty} min={0} onChange={(v) => setQty(l.key, v)} testid={`burst-qty-${l.item.code}`} /></TableCell>
              <TableCell className="text-right"><Button variant="ghost" className="h-11 w-11 p-0 text-rose-600" onClick={() => remove(l.key)} data-testid={`burst-remove-${l.item.code}`}><Trash2 className="h-4 w-4" /></Button></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function Scanner() {
  const [mode, setMode] = useState("unitaire");
  const [manual, setManual] = useState("");
  const [history, setHistory] = useState([]);
  const [burstType, setBurstType] = useState("sortie");
  const [lines, setLines] = useState([]);
  const [note, setNote] = useState("");
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const hub = useScanHub({ listen: false });

  const log = (code, ok, label) => setHistory((h) => [{ id: Date.now() + Math.random(), code, ok, label }, ...h].slice(0, 8));

  const addToBurst = async (code) => {
    let data;
    try { data = await lookupCode(code); } catch (e) { scanFeedback(false, "Erreur"); return toast.error(apiError(e)); }
    if (data.kind === "none") { scanFeedback(false, `Code inconnu : ${code}`); log(code, false, "Code inconnu"); return hub.showUnknown(code); }
    if (data.kind === "kit" && burstType !== "sortie") { scanFeedback(false, "Kit : sortie uniquement"); log(code, false, "Un kit ne peut être que sorti"); return; }
    const key = `${data.kind}:${data.item.code}`;
    setLines((prev) => {
      const found = prev.find((l) => l.key === key);
      const line = found ? { ...found, qty: (Number(found.qty) || 0) + 1, fresh: Date.now() } : { key, kind: data.kind, item: data.item, qty: 1, fresh: Date.now() };
      return [line, ...prev.filter((l) => l.key !== key)];
    });
    scanFeedback(true, `${data.item.code} +1`);
    log(code, true, data.item.designation);
  };

  const onScan = (code) => {
    setManual("");
    if (mode === "rafale") addToBurst(code);
    else { hub.handle(code); log(code, true, "Recherche"); }
  };
  useKeyboardScanner(onScan, !summary);

  const actions = lines.filter((l) => Number(l.qty) > 0 || burstType === "ajustement").map((l) => toAction(l, burstType, note));
  const check = async () => {
    setBusy(true);
    try { setSummary((await api.post("/movements/preview", { actions })).data); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/movements/batch", { actions });
      scanFeedback(true, "Lot validé");
      toast.success(`${data.movements.length} mouvement(s) enregistré(s).`);
      setLines([]); setNote(""); setSummary(null);
    } catch (e) {
      scanFeedback(false, "Lot bloqué");
      setSummary({ lines: summary?.lines || [], issues: apiIssues(e).length ? apiIssues(e) : [apiError(e)], can_confirm: false });
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="scanner-page">
      <PageHeader title="Scanner" subtitle="Douchette USB/Bluetooth ou caméra. Scannez un code-barres, un code article ou un code kit." testid="scanner-title">
        <Segmented value={mode} onChange={setMode} testid="scanner-mode" options={[{ id: "unitaire", label: "Unitaire", icon: Zap }, { id: "rafale", label: "Mode rafale", icon: ListChecks }]} />
      </PageHeader>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
        <div className="space-y-6 xl:col-span-2">
          <section className="rounded-md border-2 border-dashed border-slate-300 bg-white p-6 text-center shadow-sm sk-grid-bg" data-testid="scan-zone">
            <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-orange-500 text-slate-950 sk-pulse"><ScanLine className="h-8 w-8" /></div>
            <div className="font-display text-2xl font-extrabold uppercase" data-testid="scan-ready">Prêt à scanner</div>
            <p className="mb-5 text-sm text-slate-500">{mode === "rafale" ? "Chaque scan ajoute 1 à la liste en attente." : "Chaque scan ouvre le panneau de mouvement rapide."}</p>
            <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (manual.trim()) onScan(manual.trim()); }}>
              <Input value={manual} onChange={(e) => setManual(e.target.value)} data-scan-capture="true" placeholder="Saisir ou scanner un code" className="h-12 bg-white font-mono" data-testid="scanner-manual-input" />
              <Button type="submit" className="h-12 w-12 p-0" data-testid="scanner-manual-submit"><Search className="h-4 w-4" /></Button>
              <CameraButton iconOnly onDetected={onScan} testid="scanner-camera-button" />
            </form>
          </section>
          <Panel title="Derniers scans" testid="scan-history">
            {history.length === 0 ? <p className="p-5 text-sm text-slate-500">Aucun scan pour l'instant.</p> : (
              <ul className="divide-y">{history.map((h) => (
                <li key={h.id} className="flex items-center justify-between gap-3 px-5 py-2.5 text-sm" data-testid="scan-history-item">
                  <span className="font-mono font-semibold">{h.code}</span><span className={`truncate ${h.ok ? "text-slate-500" : "text-rose-600"}`}>{h.label}</span>
                </li>))}
              </ul>
            )}
          </Panel>
        </div>
        <div className="xl:col-span-3">
          {mode === "rafale" ? (
            <Panel title={`Liste en attente (${lines.length})`} icon={ListChecks} testid="burst-panel"
              actions={<Segmented value={burstType} onChange={setBurstType} options={BURST_TYPES} testid="burst-type" />}>
              <BurstTable lines={lines} testid="burst-table" remove={(k) => setLines(lines.filter((l) => l.key !== k))} setQty={(k, v) => setLines(lines.map((l) => (l.key === k ? { ...l, qty: v } : l)))} />
              {burstType === "ajustement" && lines.length > 0 && <p className="border-t px-5 py-3 text-xs text-slate-500">Inventaire : le stock de chaque article sera fixé à la quantité comptée.</p>}
              <div className="flex flex-col gap-3 border-t p-5 sm:flex-row">
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note pour tout le lot (optionnelle)" className="h-12 flex-1" data-testid="burst-note-input" />
                <Button variant="outline" className="h-12" disabled={!lines.length} onClick={() => setLines([])} data-testid="burst-clear-button">Vider</Button>
                <Button className="h-12 gap-2" disabled={!actions.length || busy} onClick={check} data-testid="burst-validate-button"><CheckCircle2 className="h-4 w-4" /> Vérifier et valider ({actions.length})</Button>
              </div>
            </Panel>
          ) : (
            <Panel title="Mode unitaire" icon={Zap} testid="single-mode-panel">
              <ol className="space-y-3 p-6 text-sm text-slate-600">
                <li><b className="text-slate-900">1.</b> Scannez un article : choisissez Entrée, Sortie ou Ajustement, ajustez la quantité avec + / −, puis validez.</li>
                <li><b className="text-slate-900">2.</b> Scannez un kit : le panneau de sortie de kit vérifie les composants disponibles.</li>
                <li><b className="text-slate-900">3.</b> Code inconnu : créez directement l'article avec le code-barres pré-rempli.</li>
              </ol>
            </Panel>
          )}
        </div>
      </div>
      <BurstSummary open={!!summary} summary={summary} busy={busy} onClose={() => setSummary(null)} onConfirm={confirm} />
      {hub.ui}
    </div>
  );
}
